/**
 * BrandingPage — tenant branding settings (FS-806).
 *
 * Form fields:
 *   - Logo upload       (image/png, image/jpeg, image/svg+xml, image/webp; ≤2MB)
 *   - Favicon upload    (image/png, image/x-icon, image/svg+xml; ≤256KB)
 *   - Primary color     (#RRGGBB hex picker + text entry)
 *   - Portal name       (plain text, stored locally for preview)
 *
 * The preview card on the right shows what the tenant portal will look like
 * with the currently-drafted values applied. Save commits to the backend via
 * PUT /api/v1/branding (color) + signed R2 upload for logo.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
	useBrandingSettings,
	useBrandingUpdate,
	useBrandingReplaceLogo,
	useBrandingRemoveLogo,
	usePortalName,
} from "./hooks";
import {
	isValidHexColor,
	validateLogoFile,
	validateFaviconFile,
	ALLOWED_LOGO_TYPES,
	ALLOWED_FAVICON_TYPES,
	type BrandingSettings,
} from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-sky-600 bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const HELP = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";

const DEFAULT_BRAND_COLOR = "#2563eb";

function hexForInput(v: string | undefined | null): string {
	if (!v) return DEFAULT_BRAND_COLOR;
	return isValidHexColor(v) ? v.toLowerCase() : DEFAULT_BRAND_COLOR;
}

function bytesLabel(b: number): string {
	if (b < 1024) return `${b} B`;
	if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
	return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

export function BrandingPage() {
	const settingsQuery = useBrandingSettings();
	const updateMut = useBrandingUpdate();
	const replaceLogoMut = useBrandingReplaceLogo();
	const removeLogoMut = useBrandingRemoveLogo();
	const setPortalName = usePortalName();

	const data: BrandingSettings | undefined = settingsQuery.data;

	// Local draft state, hydrated from the server snapshot.
	const [color, setColor] = useState<string>(DEFAULT_BRAND_COLOR);
	const [portalName, setPortalNameLocal] = useState<string>("");
	const [logoFile, setLogoFile] = useState<File | null>(null);
	const [logoFileError, setLogoFileError] = useState<string | null>(null);
	const [faviconFile, setFaviconFile] = useState<File | null>(null);
	const [faviconFileError, setFaviconFileError] = useState<string | null>(null);
	const [saved, setSaved] = useState(false);
	const [saveError, setSaveError] = useState<string | null>(null);

	const logoInputRef = useRef<HTMLInputElement | null>(null);
	const faviconInputRef = useRef<HTMLInputElement | null>(null);

	// One-shot hydration when settings first load.
	const hydratedRef = useRef(false);
	useEffect(() => {
		if (!data || hydratedRef.current) return;
		hydratedRef.current = true;
		setColor(hexForInput(data.brand_color));
		setPortalNameLocal(data.portal_name ?? "");
	}, [data]);

	const isPending =
		updateMut.isPending || replaceLogoMut.isPending || removeLogoMut.isPending;

	const colorValid = isValidHexColor(color);

	// Preview consumes drafted values so the user sees their edits live.
	const previewColor = colorValid ? color : DEFAULT_BRAND_COLOR;
	const previewName = portalName.trim() || "Your portal";
	const previewLogoUrl = useMemo(() => {
		if (logoFile) return URL.createObjectURL(logoFile);
		return data?.logo_url ?? null;
	}, [logoFile, data?.logo_url]);

	// Revoke object URL on change to avoid leaks.
	useEffect(() => {
		return () => {
			if (previewLogoUrl && previewLogoUrl.startsWith("blob:")) {
				URL.revokeObjectURL(previewLogoUrl);
			}
		};
	}, [previewLogoUrl]);

	const previewFaviconUrl = useMemo(
		() => (faviconFile ? URL.createObjectURL(faviconFile) : null),
		[faviconFile],
	);
	useEffect(() => {
		return () => {
			if (previewFaviconUrl) URL.revokeObjectURL(previewFaviconUrl);
		};
	}, [previewFaviconUrl]);

	function onPickLogo(e: React.ChangeEvent<HTMLInputElement>) {
		setLogoFileError(null);
		const f = e.target.files?.[0] ?? null;
		if (!f) {
			setLogoFile(null);
			return;
		}
		const v = validateLogoFile(f);
		if (!v.ok) {
			setLogoFileError(v.error);
			setLogoFile(null);
			return;
		}
		setLogoFile(f);
	}

	function onPickFavicon(e: React.ChangeEvent<HTMLInputElement>) {
		setFaviconFileError(null);
		const f = e.target.files?.[0] ?? null;
		if (!f) {
			setFaviconFile(null);
			return;
		}
		const v = validateFaviconFile(f);
		if (!v.ok) {
			setFaviconFileError(v.error);
			setFaviconFile(null);
			return;
		}
		setFaviconFile(f);
	}

	async function onSubmit(e: FormEvent) {
		e.preventDefault();
		setSaved(false);
		setSaveError(null);

		if (!colorValid) {
			setSaveError("invalid color (expected #RRGGBB)");
			return;
		}

		try {
			// 1. Persist portal name locally (no backend column yet).
			setPortalName(portalName.trim());

			// 2. Upload logo if a new file was picked.
			if (logoFile) {
				await replaceLogoMut.mutateAsync(logoFile);
			}

			// 3. Commit color. Only PUT if it differs from the server snapshot.
			const serverColor = hexForInput(data?.brand_color);
			if (color.toLowerCase() !== serverColor.toLowerCase()) {
				await updateMut.mutateAsync({ brand_color: color.toLowerCase() });
			}

			setSaved(true);
			setLogoFile(null);
			setFaviconFile(null);
			if (logoInputRef.current) logoInputRef.current.value = "";
			if (faviconInputRef.current) faviconInputRef.current.value = "";
		} catch (err) {
			setSaveError(err instanceof Error ? err.message : "save failed");
		}
	}

	async function onRemoveLogo() {
		setSaved(false);
		setSaveError(null);
		try {
			await removeLogoMut.mutateAsync();
			setLogoFile(null);
			if (logoInputRef.current) logoInputRef.current.value = "";
			setSaved(true);
		} catch (err) {
			setSaveError(err instanceof Error ? err.message : "remove failed");
		}
	}

	return (
		<main
			data-testid="branding-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
					Branding
				</h1>
				<p className="text-sm text-zinc-600">
					Upload your logo and favicon, pick a primary color, and name your
					tenant portal. Changes show up in the live preview on the right.
				</p>
			</header>

			{settingsQuery.isLoading ? (
				<div
					className={`${CARD} text-sm text-zinc-500`}
					data-testid="branding-loading"
					role="status"
				>
					Loading branding settings.
				</div>
			) : settingsQuery.isError ? (
				<div
					className={`${CARD} text-sm text-red-600`}
					data-testid="branding-error"
					role="alert"
				>
					Could not load branding settings: {settingsQuery.error.message}
				</div>
			) : (
				<div
					className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]"
					data-testid="branding-ready"
				>
					{/* ----------------------------------------------------------- */}
					{/* Form column                                                 */}
					{/* ----------------------------------------------------------- */}
					<form
						onSubmit={onSubmit}
						className={`${CARD} flex flex-col gap-6`}
						data-testid="branding-form"
						aria-label="branding settings form"
					>
						{/* Logo ---------------------------------------------------- */}
						<section className="flex flex-col gap-2" data-testid="branding-logo-section">
							<label htmlFor="branding-logo" className={LABEL}>
								Logo
							</label>
							<input
								id="branding-logo"
								ref={logoInputRef}
								type="file"
								accept={ALLOWED_LOGO_TYPES.join(",")}
								onChange={onPickLogo}
								className="text-sm text-zinc-700 file:mr-3 file:rounded-[6px] file:border file:border-zinc-300 file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-zinc-700 hover:file:bg-zinc-50"
								data-testid="branding-logo-input"
							/>
							{logoFile ? (
								<p className={HELP} data-testid="branding-logo-pending">
									Ready to upload: {logoFile.name} ({bytesLabel(logoFile.size)})
								</p>
							) : data?.logo_url ? (
								<p className={HELP} data-testid="branding-logo-current">
									Current logo attached.
								</p>
							) : (
								<p className={HELP}>No logo uploaded yet.</p>
							)}
							{logoFileError ? (
								<p className={ERROR_TEXT} data-testid="branding-logo-error">
									{logoFileError}
								</p>
							) : null}
							{data?.logo_url ? (
								<div>
									<button
										type="button"
										onClick={onRemoveLogo}
										className={BTN_GHOST}
										disabled={isPending}
										data-testid="branding-logo-remove"
									>
										Remove current logo
									</button>
								</div>
							) : null}
						</section>

						{/* Favicon ------------------------------------------------ */}
						<section className="flex flex-col gap-2" data-testid="branding-favicon-section">
							<label htmlFor="branding-favicon" className={LABEL}>
								Favicon
							</label>
							<input
								id="branding-favicon"
								ref={faviconInputRef}
								type="file"
								accept={ALLOWED_FAVICON_TYPES.join(",")}
								onChange={onPickFavicon}
								className="text-sm text-zinc-700 file:mr-3 file:rounded-[6px] file:border file:border-zinc-300 file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-zinc-700 hover:file:bg-zinc-50"
								data-testid="branding-favicon-input"
							/>
							{faviconFile ? (
								<p className={HELP} data-testid="branding-favicon-pending">
									Ready to upload: {faviconFile.name} ({bytesLabel(faviconFile.size)})
								</p>
							) : (
								<p className={HELP}>No favicon selected.</p>
							)}
							{faviconFileError ? (
								<p className={ERROR_TEXT} data-testid="branding-favicon-error">
									{faviconFileError}
								</p>
							) : null}
						</section>

						{/* Color -------------------------------------------------- */}
						<section className="flex flex-col gap-2" data-testid="branding-color-section">
							<label htmlFor="branding-color-picker" className={LABEL}>
								Primary color
							</label>
							<div className="flex items-center gap-3">
								<input
									id="branding-color-picker"
									type="color"
									value={colorValid ? color : DEFAULT_BRAND_COLOR}
									onChange={(e) => setColor(e.currentTarget.value)}
									className="h-10 w-14 cursor-pointer rounded-[6px] border border-zinc-300 bg-white p-1"
									data-testid="branding-color-picker"
									aria-label="primary color picker"
								/>
								<input
									id="branding-color-text"
									type="text"
									value={color}
									onChange={(e) => setColor(e.currentTarget.value)}
									placeholder="#2563eb"
									spellCheck={false}
									className={`${INPUT} max-w-[10rem] font-mono uppercase`}
									data-testid="branding-color-text"
									aria-label="primary color hex"
								/>
							</div>
							{!colorValid ? (
								<p className={ERROR_TEXT} data-testid="branding-color-error">
									Color must be 6-char hex (example: #2563eb).
								</p>
							) : (
								<p className={HELP}>Used for buttons and highlights in your portal.</p>
							)}
						</section>

						{/* Portal name -------------------------------------------- */}
						<section className="flex flex-col gap-2" data-testid="branding-name-section">
							<label htmlFor="branding-portal-name" className={LABEL}>
								Portal name
							</label>
							<input
								id="branding-portal-name"
								type="text"
								value={portalName}
								onChange={(e) => setPortalNameLocal(e.currentTarget.value)}
								placeholder="Acme Tracking"
								maxLength={80}
								className={INPUT}
								data-testid="branding-portal-name"
								name="portalName"
							/>
							<p className={HELP}>
								The display name shown on your tenant portal header.
							</p>
						</section>

						{/* Save row ----------------------------------------------- */}
						<div className="flex items-center gap-3 border-t border-zinc-200 pt-4">
							<button
								type="submit"
								className={BTN_PRIMARY}
								disabled={isPending}
								data-testid="branding-save"
							>
								{isPending ? "Saving." : "Save branding"}
							</button>
							{saved ? (
								<p
									className="text-sm text-emerald-700"
									role="status"
									data-testid="branding-saved"
								>
									Saved.
								</p>
							) : null}
							{saveError ? (
								<p
									className="text-sm text-red-600"
									role="alert"
									data-testid="branding-save-error"
								>
									{saveError}
								</p>
							) : null}
						</div>
					</form>

					{/* ----------------------------------------------------------- */}
					{/* Preview column                                              */}
					{/* ----------------------------------------------------------- */}
					<aside
						className={`${CARD} flex flex-col gap-4`}
						data-testid="branding-preview"
						aria-label="branding live preview"
					>
						<h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-500">
							Live preview
						</h2>

						<div
							className="overflow-hidden rounded-[8px] border border-zinc-200 bg-zinc-50"
							data-testid="branding-preview-card"
						>
							<div
								className="flex items-center gap-3 border-b border-zinc-200 px-4 py-3"
								style={{ backgroundColor: previewColor }}
							>
								{previewLogoUrl ? (
									<img
										src={previewLogoUrl}
										alt="logo preview"
										className="h-8 w-8 rounded-[6px] border border-white/40 bg-white object-contain"
										data-testid="branding-preview-logo"
									/>
								) : (
									<div
										className="flex h-8 w-8 items-center justify-center rounded-[6px] border border-white/40 bg-white text-xs font-semibold text-zinc-500"
										data-testid="branding-preview-logo-placeholder"
									>
										{previewName.slice(0, 2).toUpperCase()}
									</div>
								)}
								<p
									className="truncate text-sm font-semibold text-white"
									data-testid="branding-preview-name"
								>
									{previewName}
								</p>
							</div>

							<div className="flex flex-col gap-3 p-4">
								<div className="flex items-center gap-2">
									{previewFaviconUrl ? (
										<img
											src={previewFaviconUrl}
											alt="favicon preview"
											className="h-4 w-4 rounded-[4px] border border-zinc-200 bg-white object-contain"
											data-testid="branding-preview-favicon"
										/>
									) : (
										<span className="h-4 w-4 rounded-[4px] border border-zinc-200 bg-white" />
									)}
									<span className="text-xs text-zinc-500">Browser tab</span>
								</div>

								<button
									type="button"
									disabled
									className="w-full rounded-[6px] px-3 py-2 text-sm font-medium text-white"
									style={{ backgroundColor: previewColor }}
									data-testid="branding-preview-button"
								>
									Primary button
								</button>

								<div
									className="flex items-center gap-2 rounded-[6px] border border-zinc-200 bg-white px-3 py-2 text-xs text-zinc-700"
									data-testid="branding-preview-color-chip"
								>
									<span
										className="h-4 w-4 rounded-[4px] border border-zinc-200"
										style={{ backgroundColor: previewColor }}
									/>
									<span className="font-mono uppercase">{previewColor}</span>
								</div>
							</div>
						</div>

						<p className={HELP}>
							This is how your portal header will look to advertisers and
							affiliates. Changes apply after you click Save branding.
						</p>
					</aside>
				</div>
			)}
		</main>
	);
}

export default BrandingPage;
